<template>
  <JsonLDModel :jsonOptions="jsonOptionsModel" />      
  <NuxtLayout>
    <div class="slot-body">
      <h1 v-if="dadosSSR?.nameModel" class="ssr-model-title">
        {{ (route.params.language === 'pt-br' ? 'Perfil e Medidas de ' : 'Profile and Measurements of ') + dadosSSR.nameModel }}
      </h1>
      <NavBarHeader
        v-if="dataModel"
        :model-info="dadosSSR"
        :model-data="dataModel"
      >
        <div class="">
          <div class="row" style="margin-left:0px;margin-right:0px;">
            <div class="col-lg-5 mt-5 m-left">
              <div class="title model-info">
                {{ name }}
              </div>
              <h3 class="description mt-3 mb-4 model-info">
                {{ description }}
              </h3>
              <p class="ml-5 mt-3 text model-info">
                {{ text }}
              </p>
            </div>
            <div class="col-lg-7 img-credits">
              <!-- elemento LCP do /perfil, no desktop e no mobile -->
              <img
                class="img-perfil-model"
                :src="imgPerfil"
                :alt="imageAltText ? imageAltText : name"
                fetchpriority="high"
              />
            </div>
          </div>
        </div>
        <div id="profileModel" class="block block--gray">
          <!-- nesta aba o perfil abre inteiro, já no HTML do SSR -->
          <PerfilDescription
            v-if="dataModel?.profileQuestion"
            :model="dadosSSR"
            :perfilModel="dataModel"
            divStyle="div-profile-show"
          />
          <ClientOnly>
            <BannerAssineHorizontal :path="bannerAssineHorizontal" />
          </ClientOnly>
        </div>
      </NavBarHeader>
    </div>
  </NuxtLayout>
</template>
<script lang="ts" setup>
// SEO: página de conteúdo explícito — marcação que o Google pede para não
// classificar o site inteiro como adulto. Ver composables/useAdultRating.ts
useAdultRating();
import PerfilDescription from "@/components/Model/PerfilDescription.vue";
import BannerAssineHorizontal from "@/components/HomePage/BannerAssineHorizontal.vue";
import NavBarHeader from "@/components/Model/NavBarHeader.vue";
import api from "@/api";
import { isMobile } from "@/shared/utils/mobile";
import JsonLDModel from "@/components/JsonLDModel.vue";
import { publicPerfilPayload } from "@/utils/publicModelPayload";

definePageMeta({
  middleware: ["not-found-lang"],
});

const route = useRoute();
const storeUser = userStore();
const storePlan = planStore();

const ensaio = useEnsaioRota();
const urlPerfil = `v2/modelos/${ensaio.nome}/perfilWallpaperCredits?${ensaio.query}`;

// As abas saem das flags do próprio perfil: esta aba não busca mais as fotos
// (o `v2/.../photos` é o endpoint lento, por causa das miniaturas com marca).
const [metadados, perfilSSR] = await Promise.all([
  useEnsaioMetadados(),
  useEnsaioPublico(
    "model-perfil-public",
    `v2/modelos/${ensaio.nome}/perfilWallpaperCredits`,
    publicPerfilPayload
  ),
]);
exigirMetadados(metadados.error.value, metadados.data.value);

const dadosSSR = ref(metadados.data.value);

const canonical = useModelCanonical(route.params.name, route.params.language);
const seo = await useSeoConfig(String(route.name), String(route.params.language));
useHead({
  templateParams: {
    subTitle: null,
    subPage: (dadosSSR.value.nameModel ? dadosSSR.value.nameModel + ": " : "") + (seo.value?.title || (route.params.language === 'pt-br' ? "Perfil Completo, Biografia e Medidas" : "Full Profile, Biography and Measurements")),
    page_url: canonical,
    page_description:
      (dadosSSR.value.nameModel ? dadosSSR.value.nameModel + ": " : "") + (seo.value?.description || (route.params.language === 'pt-br' ? "Conheça o perfil completo no Bella da Semana. Biografia, medidas, curiosidades e todos os ensaios da modelo. Acesse agora!" : "Get to know the model on Bella Club. Biography, measurements, fun facts and all the model's photoshoots. Access it now!")),
    page_image: shareImage(seo.value?.image, dadosSSR.value.imgPerfil),
    page_keywords:
      seo.value?.keywords ||
      (dadosSSR.value.meta_keywords ??
      "bella, bella da semana, bela da semana, bella semana, bela semana, belladasemana, bella site, bella da semana site, bella blog"),
    page_locale: route.params.language as string,
    page_url_pt:
      "https://belladasemana.com.br/pt-br/modelos/" +
      route.params.name +
      "/photo",
    page_url_en:
      "https://belladasemana.com.br/en/modelos/" + route.params.name + "/photo",
    page_canonical: canonical,
  },
});

const jsonOptionsModel = ref({
  type: "Person",
  name: dadosSSR.value.nameModel || dadosSSR.value.seoTitleModel || "",
  jobTitle: 'Model',
  image: dadosSSR.value.imgPerfil ?? "",
  url: canonical
});


const bannerAssineHorizontal = ref("");
const showSubscribe = ref(false);
// Texto de perfil e flags das abas. O conteúdo não depende de quem vê: o
// cliente só busca quando o SSR não trouxe (navegação entre abas, falha da API).
const perfilCliente = ref<any>(null);
const dataModel = computed(() => perfilCliente.value ?? perfilSSR.data.value);
const name = computed(() => dadosSSR.value.nameModel);
const imageAltText = computed(() => dadosSSR.value.imageAltText);
const description = computed(() => dadosSSR.value.descriptionModel);
const text = computed(() => dadosSSR.value.text);
const imgPerfil = computed(() => dadosSSR.value.imgPerfil);

onMounted(() => {
  getBannerPath();
  if (!perfilSSR.data.value) carregarPerfil();
  setTimeout(() => {
    const profileModel = document.getElementById("profileModel");
    if (profileModel) profileModel.scrollIntoView();
  }, 500);
});

async function carregarPerfil() {
  const perfil = await buscarEnsaioNoCliente(urlPerfil);
  if (perfil && typeof perfil === "object" && !("message" in perfil)) {
    perfilCliente.value = perfil;
  }
}

function getBannerPath() {
  if (!sessionStorage.getItem("bannerAssineHorizontal"))
    api
      .get(
        `v1/home/getBannerSuper?mobile=${isMobile()}&language=${
          route.params.language
        }`
      )
      .then(({ data }) => {
        bannerAssineHorizontal.value = data.bannerAssineHorizontal;
        sessionStorage.setItem(
          "bannerAssineHorizontal",
          JSON.stringify(bannerAssineHorizontal.value)
        );
      });
  else
    bannerAssineHorizontal.value = JSON.parse(
      sessionStorage.getItem("bannerAssineHorizontal") ?? ""
    );
}
function showModalSubscribePlans(value: boolean) {
  if (!storeUser.isActiveUser) {
    showSubscribe.value = value;
    if (value == true)
      showModalPlans(
        "Clicar para reproduzir o video sem ter uma assinatura ativa"
      );
  }
}
function showModalPlans(acionador: string) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "paywall",
    paywall: { acionador: acionador },
  });
}
</script>
<style scoped lang="css">
.tab-panels :deep(.tab-panel) {
  width: 98%;
}
.slot-body {
  margin-top: 65px !important;
  background-color: #fff !important;
}

.title {
  font-family: "Bebas-Neue-Light";
  font-size: 60px;
  font-weight: 500;
  color: black;
}

.title-models {
  font-family: "Exo-2";
  font-size: 32px;
  font-weight: 600;
  color: #b20000;
}

.subtitle {
  font-size: 24px;
  font-weight: 600;
}

.img-perfil-model {
  width: 100%;
  right: -5px;
}

.img-credits {
  padding-right: 17px !important;
}

.model-info {
  padding-left: 108px;
}

.title {
  color: #000000;
  font-family: "Bebas-Neue";
  font-size: 72px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 72px;
}

.description {
  color: #b20000;
  font-family: "Roboto-Medium";
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 28px;
}

.ssr-model-title {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.text {
  color: #5b5858;
  font-family: "Roboto-Regular";
  font-size: 16px;
  letter-spacing: 0;
  line-height: 19px;
  max-width: 370px;
}

/* regras mobile 769vp */
@media screen and (max-width: 769px) {
  .m-left {
    display: none;
  }
  .img-perfil-model {
    width: 105%;
    right: unset;
    left: -5px;
  }
}
</style>
